import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getGeolocation,
  getLocalKeyPair,
  WORKSTATION_AUTH_STATUS,
} from '../../../../services/workstationAuth';

// Concentra o estado e a orquestração do cadastro de estação de trabalho,
// deixando LoginWorkstationRegister apenas com a camada visual.
const useWorkstationRegistration = ({
  email,
  partnerContextId,
  condominiumContextId,
  auth,
  setCurrentStep,
  steps,
}) => {
  const [t] = useTranslation('login');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Quando já existe keypair local nesta máquina, o Rejected vindo do backend
  // significa "aprovação expirou ou foi revogada" — copy diferente de primeiro
  // acesso, e a chave precisa ser trocada no cadastro (ver `rotateKey`).
  const [hasLocalKeyPair, setHasLocalKeyPair] = useState(false);
  // A troca acontece uma vez por passagem nesta tela: num retry (falha de rede
  // ou geolocalização) a chave recém-gerada é reaproveitada em vez de virar
  // outra — duas chaves seriam duas solicitações pendentes para o supervisor.
  const alreadyRotated = useRef(false);

  useEffect(() => {
    let cancelled = false;
    getLocalKeyPair().then(keyPair => {
      if (!cancelled) setHasLocalKeyPair(!!keyPair);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRegister = async () => {
    setSubmitting(true);
    setError(null);

    let coords;
    try {
      coords = await getGeolocation();
    } catch (err) {
      setError(t('workstation.errors.geolocation'));
      setSubmitting(false);
      return;
    }

    const rotateKey = hasLocalKeyPair && !alreadyRotated.current;
    if (rotateKey) alreadyRotated.current = true;

    // Nunca os dois juntos: condomínio tem prioridade quando presente (o
    // backend nunca devolve os dois contextos preenchidos ao mesmo tempo).
    const contextParams = condominiumContextId
      ? { condominiumContextId }
      : { partnerContextId };

    try {
      const status = await auth.registerWorkstation({
        username: email,
        latitude: coords.latitude,
        longitude: coords.longitude,
        ...contextParams,
        rotateKey,
      });
      if (
        status === WORKSTATION_AUTH_STATUS.PENDING ||
        status === WORKSTATION_AUTH_STATUS.APPROVED
      ) {
        setCurrentStep(steps.workstationPending);
      } else {
        setError(t('workstation.errors.register-failed'));
      }
    } catch (err) {
      setError(t('workstation.errors.register-failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const scope = hasLocalKeyPair ? 'reapprove' : 'register';

  return { submitting, error, scope, handleRegister };
};

export default useWorkstationRegistration;
